import React, { useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { debug } from "../../../utils/debugLogger";
import { ConnectableSite, SitePickerProvider } from "../types";
import SiteCard from "./SiteCard";

interface SitePickerProps<S extends ConnectableSite> {
  provider: SitePickerProvider<S>;
  sites: S[];
  /** Customer the grant was started for; every site is connected to this one. */
  customerId: string;
  /** Account that granted access, so a wrong login is obvious before saving. */
  email?: string;
  onClose: () => void;
}

function SitePicker<S extends ConnectableSite>({
  provider,
  sites,
  customerId,
  email,
  onClose,
}: SitePickerProps<S>) {
  const { idToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [connected, setConnected] = useState<Record<string, boolean>>({});
  const [savingSiteUrl, setSavingSiteUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { one, many } = provider.siteNoun;

  const filteredSites = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sites;
    return sites.filter((s) => s.site_url.toLowerCase().includes(term));
  }, [sites, searchTerm]);

  const handleConnect = async (site: S) => {
    if (!idToken) {
      setError("Your session has expired. Please sign in again.");
      return;
    }
    setSavingSiteUrl(site.site_url);
    setError(null);
    try {
      await provider.save(customerId, [site.site_url], idToken);
      // Local flip only: the Connect tab re-reads the customer on return, so there is
      // nothing to refetch here.
      setConnected((prev) => ({ ...prev, [site.site_url]: true }));
    } catch (err: any) {
      debug.error(`Failed to connect ${provider.shortLabel} ${one}:`, err);
      setError(err?.message || `Failed to connect the ${one}. Please try again.`);
    } finally {
      setSavingSiteUrl(null);
    }
  };

  return (
    <>
      <header className="mb-8 pb-8 border-b border-gray-100">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
          Connect {provider.label}
        </h1>
        <p className="text-sm text-gray-500">
          Choose the {many} to connect
          {email ? (
            <>
              {" from "}
              <span className="font-medium text-gray-700">{email}</span>
            </>
          ) : null}
          .
        </p>
      </header>

      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">
          Available {provider.shortLabel} {many}
        </h2>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder={`Search ${many}`}
            aria-label={`Search ${many}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:ring-brand-primary focus:border-brand-primary text-sm"
          />
        </div>
      </div>

      {error && <p role="alert" className="text-sm text-red-600 mb-3">{error}</p>}

      {filteredSites.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">
          No {many} found. Try a different search term.
        </p>
      ) : (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {filteredSites.map((site) => (
            <SiteCard
              key={site.site_url}
              {...provider.describeSite(site)}
              onConnect={() => handleConnect(site)}
              isConnected={!!connected[site.site_url]}
              isSaving={savingSiteUrl === site.site_url}
            />
          ))}
        </section>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
        >
          Done
        </button>
      </div>
    </>
  );
}

export default SitePicker;
